/**
 * @file dashboardService.js
 * @description This file contains the code for fetching data for the individual Dashboards.
 */

import { clientApiHttp } from './httpService';
import { normalizeDatesToUtc } from '../utils/utils';

const getTenantId = () => +window.localStorage.getItem('tenantId');

/**
 * Builds query parameters for the given list of population group IDs.
 * @param {Array<number>} populationGroupIds - An array of population group IDs.
 * @returns {string} - A string of populationGroupIds used for query parameters.
 * @example "populationGroupIds=270&populationGroupIds=291"
 */
const buildPopulationGroupQueryParams = populationGroupIds => {
    return populationGroupIds && populationGroupIds.length > 0
        ? populationGroupIds.map(id => `populationGroupIds=${id}`).join('&')
        : '';
};

/**
 * Retrieves all Dashboard data for a specific Dashboard based on the given parameters.
 *
 * @param {number} tenantId - The ID of the tenant.
 * @param {string} startDate - The start date for the dashboard data given in string format.
 * @param {string} endDate - The end date for the dashboard data given in string format.
 * @param {Array<number>} populationGroupIds - An array of population group IDs.
 * @param {number} dashboardTypeId - The ID type of the dashboard, which is the number mapped from the DashboardTypeEnum.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error retrieving the contacts.
 */
export const getDashboardData = async (
    tenantId,
    startDate,
    endDate,
    populationGroupIds,
    dashboardTypeId,
    queryOptions = {},
) => {
    if (!tenantId) {
        tenantId = getTenantId();
    }
    const { startDateUTC, endDateUTC } = normalizeDatesToUtc(
        startDate,
        endDate,
    );
    const populationGroupParams =
        buildPopulationGroupQueryParams(populationGroupIds);
    const params = new URLSearchParams({
        tenantId: String(tenantId),
        startDate: startDateUTC,
        endDate: endDateUTC,
        dashboardTypeId: String(dashboardTypeId),
    });

    if (populationGroupParams) {
        populationGroupParams.split('&').forEach(paramPair => {
            const [key, value] = paramPair.split('=');
            if (key && value) {
                params.append(key, value);
            }
        });
    }

    if (queryOptions?.page !== undefined && queryOptions?.page !== null) {
        params.append('page', String(queryOptions.page));
    }

    if (
        queryOptions?.pageSize !== undefined &&
        queryOptions?.pageSize !== null
    ) {
        params.append('pageSize', String(queryOptions.pageSize));
    }

    if (queryOptions?.sortBy) {
        params.append('sortBy', queryOptions.sortBy);
    }

    if (queryOptions?.sortOrder) {
        params.append('sortOrder', queryOptions.sortOrder);
    }

    try {
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/reporting/dashboard?${params.toString()}`,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to retrieve dashboard data');
    }
};

/**
 * Retrieves all Dashboard data in a single API request.
 *
 * @param {number} tenantId - The ID of the tenant.
 * @param {string} startDate - The start date for the dashboard data given in string format.
 * @param {string} endDate - The end date for the dashboard data given in string format.
 * @param {Array<number>} populationGroupIds - An array of population group IDs.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error retrieving dashboard data.
 */
export const getDashboardAllData = async (
    tenantId,
    startDate,
    endDate,
    populationGroupIds,
) => {
    if (tenantId === null || tenantId === undefined || tenantId === '') {
        tenantId = getTenantId();
    }
    const { startDateUTC, endDateUTC } = normalizeDatesToUtc(
        startDate,
        endDate,
    );
    const populationGroupParams =
        buildPopulationGroupQueryParams(populationGroupIds);
    const params = `tenantId=${tenantId}&startDate=${startDateUTC}&endDate=${endDateUTC}&${populationGroupParams}`;
    try {
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/reporting/dashboard/all?${params}`,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to retrieve dashboard data');
    }
};

export const getMultitenantDashboardAllData = async (
    clientIds,
    startDate,
    endDate,
) => {
    const { startDateUTC, endDateUTC } = normalizeDatesToUtc(
        startDate,
        endDate,
    );
    const params = `clientIds=${clientIds.join('&clientIds=')}&startDate=${startDateUTC}&endDate=${endDateUTC}`;
    try {
        const response = await clientApiHttp.get(
            `/v2/client/reporting/dashboard/all?${params}`,
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to retrieve dashboard data');
    }
};
